import { Component, HostListener, OnInit, ViewChild } from '@angular/core'
import { MatPaginator } from '@angular/material/paginator'
import { BuilderService } from 'src/app/builder-services/builder.service'
import { AppSettingsService } from 'src/app/settings/services/app-settings.service'
import { PushNotificationsService } from '../push-notifications.service'
import { MatTableDataSource } from '@angular/material/table'
import { formatDate } from '@angular/common'
import { Observable } from 'rxjs'
import { map } from 'rxjs/operators'
import { VendorService } from 'src/app/multivendor/vendor.service'

export interface TypeObject {
  type: string
  message: string // Ensure message is always present, but it can be an empty string initially.
}

export interface PeriodicElement {
  id?: number
  date: string
  name: string
  types: string[]
  selectedType?: string
  selectedMessage?: string
}

@Component({
  selector: 'app-pn-automation',
  templateUrl: './pn-automation.component.html',
  styleUrl: './pn-automation.component.scss'
})
export class PnAutomationComponent implements OnInit {
  @ViewChild(MatPaginator) paginator!: MatPaginator
  public chosenEvents: PeriodicElement[] = []
  ELEMENT_DATA: PeriodicElement[] = []
  myDate: string = ''
  project_id = this.builderService.getSelectedProject()
  // MatPaginator Inputs
  length = 1
  pageSize = 5
  foundItem: PeriodicElement[] = []
  searchKey: string = ''

  usersList: any = []
  isExcistData!: boolean
  newEventData: boolean = false
  dropdownOpen: { [key: string]: boolean } = {}
  dropdownPosition: Record<string, { top: number; left: number; width: number }> = {}
  dropdownTriggers: Record<string, HTMLElement> = {}
  displayedColumns: string[] = ['id', 'date', 'name', 'types', 'message', 'delete']
  dataSource = new MatTableDataSource<PeriodicElement>(this.ELEMENT_DATA)

  constructor(private appSettingsService: AppSettingsService, public builderService: BuilderService, public pnService: PushNotificationsService, public vendor: VendorService) {
    this.dataSource.paginator = this.paginator
    this.length = this.chosenEvents.length
  }

  ngOnInit(): void {
    this.builderService.showUpdateProgress = true
    this.appSettingsService.getSettingsByType(String(this.project_id), 'automation').then((settings) => {
      const raw = settings['events']
      const response = raw ? (typeof raw === 'string' ? JSON.parse(raw) : raw) : null
      if (response && response.length > 0) {
        this.chosenEvents = response
        this.updateTableData();
      } else {
        this.chosenEvents=this.pnService.defaultEvents;
        this.updateTableData();
      }
    })
    this.builderService.showUpdateProgress = false
  }

  // Update table data and paginator
  private updateTableData(): void {
    this.dataSource.data = [...this.chosenEvents];
    this.dataSource.paginator = this.paginator;
    this.length = this.chosenEvents.length;
    this.builderService.showUpdateProgress = false; // Hide progress after update
  }
  indexGetter(arr: any) {
    let i
    for (i = 0; i < arr.length; i++) {}
    return i
  }

  /**
   * Adds a new event to the automation list.
   * Before adding the event, it checks if push notification data exists in the database.
   * If the data exists, a new event is created with the current date, an empty name,
   * the available types from the Push Notifications service, and an empty message.
   * The new event is then added to the chosenEvents array and the MatTableDataSource is updated.
   * If the data does not exist, an alert is displayed asking the user to add push notification data.
   */
  addEvent(): void {
    this.checkPushNotificationData().subscribe((exists) => {
      if (!exists) {
        alert('Please Add Push Notification Data')
        return
      }
      let newEvent: PeriodicElement = {
        id: this.indexGetter(this.chosenEvents) + 1,
        date: formatDate(new Date(), 'dd/MM/yyyy', 'en'),
        name: '',
        types: this.pnService.typeOptions,
        selectedType: '',
        selectedMessage: ''
      }
      this.newEventData= true;
      this.chosenEvents.push(newEvent)
      this.dataSource.data = [...this.chosenEvents]
    })
  }

  /**
   * Deletes an event from the automation list at the specified index.
   * @param index - The index of the event to be deleted.
   * @returns {void}
   */
  delete(index: number): void {
    this.chosenEvents.splice(index, 1)
    for (let i = 0; i < this.chosenEvents.length; i++) {
      this.chosenEvents[i].id = i + 1
    }
    this.newEventData= true;
    this.dataSource.data = [...this.chosenEvents]
  }

  /**
   * Saves the current automation events to the database.
   * This function sets the `showUpdateProgress` flag to true to indicate that an update is in progress.
   * After that, it updates the 'projects/{project_id}/automation' path in the database with the `chosenEvents` array.
   * Once the update is complete, it sets the `showUpdateProgress` flag to false.
   *
   * @returns {void}
   */
  save(): void {
    this.newEventData=false;
    this.builderService.showUpdateProgress = true
    this.appSettingsService.upsertSettingsByType(String(this.project_id), 'automation', { events: JSON.stringify(this.chosenEvents) }).then((response) => {
      if (this.vendor.isMultiVendor) this.vendor.syncSuperVendorSettingsToVendors()
      this.builderService.showUpdateProgress = false
    })
  }

  /**
   * Handles the name change event for a specific event in the automation list.
   * When the name of an event is changed, this function updates the available types for that event
   * and sets the selected type to the first option in the types array.
   * @param element - The event for which the name change event occurred.
   * @returns {void}
   */
  onNameChange(element: PeriodicElement): void {
    element.types = this.pnService.typeOptions
    element.selectedType = element.types[0]
    this.newEventData= true;
  }

  onTypeChange(element: PeriodicElement): void {
    const selectedType = element.selectedType
    this.newEventData= true;
    // if (selectedType) {
    //   element.selectedType = selectedType;
    // }
  }

  /**
   * Handles the Message change event for a specific event in the automation list.
   * When the message of an event is changed
   * @param element - The event for which the name change event occurred.
   * @returns {void}
   */
  onMessageChange(element: PeriodicElement):void{
    this.newEventData= true;
  }
  /**
   * Handles the search functionality for events in the automation list.
   * This function filters the events based on the search key provided by the user.
   * If a matching event is found, it updates the MatTableDataSource with the found event.
   * If no matching event is found, it displays an alert message and resets the MatTableDataSource to the original list.
   *
   * @returns {void}
   */
  onSubmit() {
    this.dataSource.data = this.chosenEvents
    this.newEventData= true;
    this.foundItem = []
    this.dataSource.data.forEach((item: PeriodicElement) => (item.name.toLowerCase() === this.searchKey .toLowerCase() ? this.foundItem.push(item) : ''))
    if (this.foundItem.length) {
      this.dataSource.data = this.foundItem
    } else {
      alert('Not found')
      this.dataSource.data = this.chosenEvents
    }
  }

  toggleCellDropdown(key: string, event: Event): void {
    event.stopPropagation()
    const wasOpen = !!this.dropdownOpen[key]
    this.closeDropdowns()
    if (!wasOpen) {
      const trigger = event.currentTarget as HTMLElement
      const rect = trigger.getBoundingClientRect()
      this.dropdownTriggers[key] = trigger
      this.dropdownPosition[key] = { top: rect.bottom + 4, left: rect.left, width: rect.width }
      this.dropdownOpen[key] = true
    }
  }

  isCellDropdownOpen(key: string): boolean {
    return !!this.dropdownOpen[key]
  }

  selectCellOption(element: PeriodicElement, field: 'type' | 'name', value: string, event: Event): void {
    event.stopPropagation()
    if (field === 'type') {
      element.selectedType = value
      this.onTypeChange(element)
    } else {
      element.name = value
      this.onNameChange(element)
    }
    this.closeDropdowns()
  }

  @HostListener('document:click')
  closeDropdowns(): void {
    this.dropdownOpen = {}
    this.dropdownPosition = {}
    this.dropdownTriggers = {}
  }

  @HostListener('window:scroll')
  onScroll(): void {
    Object.keys(this.dropdownOpen).forEach(key => {
      if (this.dropdownOpen[key] && this.dropdownTriggers[key]) {
        const rect = this.dropdownTriggers[key].getBoundingClientRect()
        this.dropdownPosition[key] = { top: rect.bottom + 4, left: rect.left, width: rect.width }
      }
    })
  }

  /**
   * Checks if push notification data exists in the database.
   * @returns An Observable that emits a boolean value indicating whether the data exists.
   *          - `true`: The data exists and is valid.
   *          - `false`: The data does not exist or is invalid.
   */
  checkPushNotificationData(): Observable<boolean> {
    return new Observable<boolean>((observer) => {
      this.appSettingsService.getSettingsByType(String(this.project_id), 'application_settings').then((response) => {
        // Check if the fields are present in the response
        if (response['android_sender_id'] && response['databaseURL'] && response['serviceAccBase64'] && (response['jsonFileBase64'] || response['plistFileBase64'])) {
          // Set the isExcistData flag to true and return true
          this.isExcistData = true
          observer.next(true)
        } else {
          // Set the isExcistData flag to false and return false
          this.isExcistData = false
          observer.next(false)
        }
        observer.complete()
      }).catch(() => {
        observer.next(false)
        observer.complete()
      })
    })
  }
}
